import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, MapPin, Phone, Mail, Instagram, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { BUSINESS } from "@/data/business";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    subject: "General Inquiry",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! We'll get back to you shortly.");
      setForm({ name: "", phone: "", email: "", subject: "General Inquiry", message: "" });
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-slate-50 py-16 md:py-24" data-testid="contact-section">
      <div className="container-b grid grid-cols-1 gap-10 lg:grid-cols-5 lg:gap-14">
        <div className="lg:col-span-2">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">Get in touch</div>
          <h2 className="font-heading mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            Have a question? We&apos;re happy to help.
          </h2>
          <p className="mt-3 text-base text-slate-600">
            Send us a message and our team will reach out. For urgent enquiries, call us directly or
            connect on WhatsApp.
          </p>

          <div className="mt-8 space-y-4">
            <InfoRow
              icon={<MapPin className="h-5 w-5" />}
              label="Store address"
              value={BUSINESS.address}
              testid="contact-address"
            />
            <InfoRow
              icon={<Phone className="h-5 w-5" />}
              label="Service contact"
              value={BUSINESS.phone}
              href={`tel:${BUSINESS.phoneRaw}`}
              testid="contact-phone"
            />
            <InfoRow
              icon={<Mail className="h-5 w-5" />}
              label="Email"
              value={BUSINESS.email}
              href={`mailto:${BUSINESS.email}`}
              testid="contact-email"
            />
            <InfoRow
              icon={<Instagram className="h-5 w-5" />}
              label="Instagram"
              value={BUSINESS.instagramHandle}
              href={BUSINESS.instagram}
              external
              testid="contact-instagram"
            />
          </div>
        </div>

        <div className="lg:col-span-3">
          <form
            onSubmit={onSubmit}
            className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field label="Your name" required>
                <Input
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  placeholder="Rahul Kumar"
                  required
                  data-testid="contact-input-name"
                />
              </Field>
              <Field label="Phone number" required>
                <Input
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="9876543210"
                  required
                  data-testid="contact-input-phone"
                />
              </Field>
              <Field label="Email" required>
                <Input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="you@example.com"
                  required
                  data-testid="contact-input-email"
                />
              </Field>
              <Field label="Subject">
                <Input
                  name="subject"
                  value={form.subject}
                  onChange={onChange}
                  placeholder="Lagan Combo enquiry"
                  data-testid="contact-input-subject"
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Message" required>
                  <Textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={5}
                    placeholder="Tell us what you're looking for..."
                    required
                    data-testid="contact-input-message"
                  />
                </Field>
              </div>
            </div>

            <div className="mt-6 flex flex-col-reverse items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-slate-500">
                By submitting this form you agree to be contacted regarding your enquiry.
              </p>
              <Button
                type="submit"
                disabled={loading}
                className="rounded-full bg-blue-700 px-6 text-white hover:bg-blue-800"
                data-testid="contact-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Send message <Send className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">
        {label} {required && <span className="text-blue-700">*</span>}
      </Label>
      {children}
    </label>
  );
}

function InfoRow({ icon, label, value, href, external, testid }) {
  const inner = (
    <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 lift">
      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
        {icon}
      </span>
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
        <div className="mt-0.5 text-sm font-semibold leading-snug text-slate-900">{value}</div>
      </div>
    </div>
  );
  if (!href) return <div data-testid={testid}>{inner}</div>;
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      data-testid={testid}
      className="block"
    >
      {inner}
    </a>
  );
}
